import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { toast } from "sonner";
import TwinButterfly from "@/components/TwinButterfly";

const TRI_STOPS = [0, 0.15, 0.3, 0.45, 0.6, 0.75, 0.9, 1];
const TRI_XS =    ["80%", "14%", "82%", "18%", "84%", "16%", "80%", "22%"];
const TRI_YS =    ["30%", "58%", "26%", "62%", "24%", "60%", "30%", "60%"];

const R = ({ children, d = 0, c = "" }) => (
  <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, delay: d, ease: [0.16, 1, 0.3, 1] }} className={c}>{children}</motion.div>
);

const SIGNALS = ["APOB", "GENETIC PROFILE", "DEXA", "CGM", "VO₂ MAX", "NUTRITION", "RESISTANCE TRAINING", "SLEEP"];

const TIMELINE = [
  { t: "TIMEPOINT 01", items: ["ApoB high", "DEXA · low lean mass", "VO₂ · 34"], ctx: "The starting point on paper" },
  { t: "TIMEPOINT 02", items: ["ApoB down 22%", "Lean mass up 1.4kg", "Sleep steadier"], ctx: "Direction confirmed by real days" },
  { t: "TIMEPOINT 03", items: ["VO₂ · 41", "Trajectory clear", "Reassess plan"], ctx: "A trend the room can act on" },
];

const LOOP = [
  ["MEASURE", "Bloodwork, DEXA, VO₂. What the clinic can only see."],
  ["CONNECT", "Sleep, training, honest days. Life meets the labs."],
  ["UNDERSTAND", "One picture instead of six PDFs."],
  ["ACT", "A plan the person can actually live."],
  ["OBSERVE", "The twin watches how the plan lands, day by day."],
  ["REASSESS", "New tests confirm the direction. Or change it."],
];

const ORG_TYPES = ["Longevity clinic", "Preventive-health clinic", "Hospital / health system", "Wellness centre", "Corporate health", "Other"];

/* --------------------------------------------------------------------- */
/* §04 · Continuous Loop, pinned scroll-driven highlight                   */
/* --------------------------------------------------------------------- */
const LoopScrollTrack = ({ items }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const t = Math.min(1, Math.max(0, (v - 0.05) / 0.85));
    const next = Math.min(items.length - 1, Math.floor(t * items.length));
    setActive((cur) => (cur === next ? cur : next));
  });
  const railFill = useTransform(scrollYProgress, [0.05, 0.9], ["0%", "100%"]);

  return (
    <section
      ref={ref}
      data-testid="trifinity-loop"
      className="relative bg-ink-2/50"
      style={{ height: "260vh" }}
    >
      <div className="sticky top-0 flex h-screen items-center overflow-hidden px-6 md:px-12">
        <div className="mx-auto w-full max-w-[1500px]">
          <div>
            <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
              04 · THE CONTINUOUS LOOP
            </p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-light leading-tight md:text-5xl">
              Longevity is a continuous{" "}
              <span className="text-signal">process</span>.
            </h2>
            <p className="mt-5 max-w-lg text-sm leading-relaxed text-fog md:text-base">Every cycle adds context. What looked like an outlier at Timepoint 01 becomes a trend by Timepoint 03. The twin sharpens with every real day between visits.</p>
          </div>

          {/* Progress rail */}
          <div className="mt-8 flex items-center gap-4">
            <p className="font-mono text-[9px] tracking-[0.24em] text-ash">
              {String(active + 1).padStart(2, "0")} / 06
            </p>
            <div className="relative h-px flex-1 bg-line">
              <motion.div
                style={{ width: railFill }}
                className="absolute left-0 top-0 h-px origin-left bg-signal"
              />
            </div>
            <p className="font-mono text-[9px] tracking-[0.24em] text-signal">
              {items[active][0]}
            </p>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
            {items.map(([t, d], i) => (
              <div
                key={t}
                data-testid={`trifinity-loop-${t.toLowerCase()}`}
                aria-current={i === active}
                className={`relative h-full rounded-xl border p-5 transition-all duration-500 ${
                  i === active
                    ? "border-signal bg-signal/10 shadow-[0_0_0_1px_rgba(210,55,139,0.35)]"
                    : "border-line bg-ink-2/40"
                }`}
              >
                <p
                  className={`font-mono text-[9px] tracking-[0.2em] transition-colors duration-500 ${
                    i === active ? "text-signal" : "text-ash"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-3 font-display text-xl font-light text-white">
                  {t}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-fog">{d}</p>
                {i === active && (
                  <motion.span
                    layoutId="tri-loop-active"
                    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                    className="pointer-events-none absolute inset-x-4 -bottom-0.5 h-px bg-signal"
                  />
                )}
              </div>
            ))}
          </div>

          <p className="mt-8 text-center font-mono text-[10px] tracking-[0.24em] text-ash">
            REASSESS <span className="text-signal">↻</span> FEEDS BACK INTO MEASURE · EVERYTHING CONNECTS
          </p>
        </div>
      </div>
    </section>
  );
};

const B2BForm = () => {
  const [sent, setSent] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    setSent(true);
    toast.success("Thank you. Our team will be in touch about Trifinity.");
  };
  if (sent)
    return (
      <p data-testid="trifinity-form-success" className="rounded-xl border border-signal/40 bg-ink-2/50 p-8 text-center font-display text-2xl font-light">Thank you. Our team will be in touch about bringing Trifinity to your organisation.</p>
    );
  return (
    <form data-testid="trifinity-b2b-form" onSubmit={submit} className="grid gap-4 rounded-2xl border border-line bg-ink-2/50 p-6 md:grid-cols-2 md:p-10">
      <input required data-testid="trifinity-form-name" placeholder="Name" className="rounded-md border border-line bg-transparent px-4 py-3 text-sm outline-none focus:border-signal" />
      <input required type="email" data-testid="trifinity-form-email" placeholder="Work email" className="rounded-md border border-line bg-transparent px-4 py-3 text-sm outline-none focus:border-signal" />
      <input required data-testid="trifinity-form-org" placeholder="Organisation" className="rounded-md border border-line bg-transparent px-4 py-3 text-sm outline-none focus:border-signal" />
      <select data-testid="trifinity-form-type" className="rounded-md border border-line bg-transparent px-4 py-3 text-sm outline-none focus:border-signal">
        {ORG_TYPES.map((o) => <option key={o}>{o}</option>)}
      </select>
      <input data-testid="trifinity-form-location" placeholder="City / Country" className="rounded-md border border-line bg-transparent px-4 py-3 text-sm outline-none focus:border-signal md:col-span-2" />
      <textarea data-testid="trifinity-form-message" placeholder="Message" rows={3} className="rounded-md border border-line bg-transparent px-4 py-3 text-sm outline-none focus:border-signal md:col-span-2" />
      <button type="submit" data-testid="trifinity-form-submit" className="bg-white px-8 py-4 font-mono text-[11px] tracking-[0.16em] text-ink transition-colors duration-300 hover:bg-signal hover:text-white md:col-span-2">TALK TO TRIFIT</button>
    </form>
  );
};

/* --------------------------------------------------------------------- */
/* Timepoint chart · a tiny SVG per trajectory box                          */
/* --------------------------------------------------------------------- */
const TIMEPOINT_PATHS = [
  {
    d: "M4 44 L18 46 L34 42 L50 48 L68 44 L86 42 L104 46 L120 44",
    fill: "M4 44 L18 46 L34 42 L50 48 L68 44 L86 42 L104 46 L120 44 L120 56 L4 56 Z",
    marker: [104, 46],
  },
  {
    d: "M4 48 L18 46 L34 42 L50 40 L68 34 L86 30 L104 26 L120 22",
    fill: "M4 48 L18 46 L34 42 L50 40 L68 34 L86 30 L104 26 L120 22 L120 56 L4 56 Z",
    marker: [120, 22],
  },
  {
    d: "M4 50 L18 44 L34 38 L50 30 L68 24 L86 18 L104 14 L120 10",
    fill: "M4 50 L18 44 L34 38 L50 30 L68 24 L86 18 L104 14 L120 10 L120 56 L4 56 Z",
    marker: [120, 10],
  },
];

const TimepointChart = ({ idx }) => {
  const p = TIMEPOINT_PATHS[idx];
  if (!p) return null;
  return (
    <div className="mt-4 rounded-md border border-line/70 bg-[#0C060A] p-3">
      <svg viewBox="0 0 124 60" className="h-16 w-full" aria-hidden="true" preserveAspectRatio="none">
        <defs>
          <linearGradient id={`grad-tp-${idx}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D2378B" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#D2378B" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1="0" y1="56" x2="124" y2="56" stroke="#2A2026" strokeWidth="0.5" />
        <motion.path d={p.fill} fill={`url(#grad-tp-${idx})`} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 1, delay: 0.2 }} />
        <motion.path d={p.d} fill="none" stroke="#D2378B" strokeWidth="1.5" strokeLinecap="round" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }} />
        <motion.circle cx={p.marker[0]} cy={p.marker[1]} r="2.5" fill="#D2378B" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 1.3 }} />
      </svg>
      <div className="mt-1 flex justify-between font-mono text-[8px] tracking-[0.18em] text-ash/70">
        <span>T{idx}</span>
        <span>NOW</span>
      </div>
    </div>
  );
};

const Trifinity = () => {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 900], [0, 140]);
  return (
  <main data-testid="trifinity-page" className="relative z-10 text-white">
    <TwinButterfly stops={TRI_STOPS} xs={TRI_XS} ys={TRI_YS} />
    {/* 01 EVERYTHING CONNECTS */}
    <section data-testid="trifinity-hero" className="relative flex min-h-screen items-center overflow-hidden pt-20">
      <motion.img style={{ y: heroY }} src="/assets/trifinity_person.jpg" alt="A healthy adult during a clinical fitness assessment" className="absolute inset-0 h-full w-full object-cover object-[80%_center]" />
      <div className="absolute inset-0 bg-ink/45" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-transparent" />
      <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 md:px-12">
        <p className="font-mono text-[11px] tracking-[0.28em] text-ash">TRIFINITY</p>
        <h1 data-testid="trifinity-hero-headline" className="mt-8 max-w-2xl font-display text-5xl font-light leading-[1.02] sm:text-6xl lg:text-7xl">Everything <span className="text-signal">connects</span>.</h1>
        <p className="mt-8 max-w-md text-base leading-relaxed text-fog">One ApoB reading is a photograph. A year of ApoB alongside sleep, training and honest life is a story. Trifinity is how clinics read the story.</p>
        <div className="mt-10 flex flex-wrap items-center gap-6">
          <a href="#trifinity-contact" data-testid="trifinity-hero-b2b-cta" className="bg-white px-8 py-4 font-mono text-[11px] tracking-[0.16em] text-ink transition-colors duration-300 hover:bg-signal hover:text-white">BRING TRIFINITY TO YOUR CLINIC</a>
          <a href="#trifinity-contact" data-testid="trifinity-hero-find-cta" className="font-mono text-[10px] tracking-[0.2em] text-fog underline-offset-4 hover:underline">FIND TRIFINITY →</a>
        </div>
      </div>
    </section>

    {/* 02 SNAPSHOT ISN'T A TRAJECTORY */}
    <section data-testid="trifinity-trajectory" className="bg-ink-2/50 px-6 py-24 md:px-12 md:py-32">
      <div className="mx-auto max-w-[1500px]">
        <R>
          <p className="font-mono text-[11px] tracking-[0.28em] text-ash">02 · TRAJECTORY</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-light leading-tight md:text-5xl">A snapshot isn&apos;t a <span className="text-signal">trajectory</span>.</h2>
        </R>
        <div className="mt-12 grid items-stretch gap-6 md:grid-cols-3">
          {TIMELINE.map((tp, i) => (
            <R key={tp.t} d={0.12 * i}>
              <div data-testid={`trifinity-timepoint-${i + 1}`} className={`flex h-full flex-col rounded-xl border p-6 ${i === 2 ? "border-signal/50 bg-signal/5" : "border-line bg-ink-2/40"}`}>
                <p className="font-mono text-[10px] tracking-[0.22em] text-signal">{tp.t}</p>
                {/* Timepoint chart */}
                <TimepointChart idx={i} />
                <ul className="mt-4 space-y-2">
                  {tp.items.map((x) => <li key={x} className="border-l-2 border-line pl-3 text-sm text-fog">{x}</li>)}
                </ul>
                <p className="mt-auto pt-4 font-mono text-[9px] leading-relaxed tracking-[0.12em] text-ash">{tp.ctx.toUpperCase()}</p>
              </div>
            </R>
          ))}
        </div>
      </div>
    </section>

    {/* 03 CLINIC + LIFE */}
    <section data-testid="trifinity-clinic" className="px-6 py-24 md:px-12 md:py-32">
      <div className="mx-auto max-w-[1500px]">
        <R>
          <p className="font-mono text-[11px] tracking-[0.28em] text-ash">03 · CLINIC + LIFE</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-light leading-tight md:text-5xl">Care continues past the <span className="text-signal">door</span>.</h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-fog">The knee still aches on the walk home from the office. The training block still gets skipped in a week of bad sleep. The twin keeps watching. The practitioner picks it up at the next visit exactly where the last one left off.</p>
        </R>

        {/* Connected diagram */}
        <div className="relative mt-16 grid items-center gap-6 lg:grid-cols-[1fr_auto_1fr] lg:gap-4">
          <R>
            <div data-testid="trifinity-clinic-side" className="relative rounded-xl border border-line bg-ink-2/50 p-6">
              <p className="font-mono text-[10px] tracking-[0.22em] text-signal">THE CLINIC</p>
              <ul className="mt-4 space-y-2 text-sm text-fog">{["Bloodwork", "DEXA", "VO₂ max"].map((x) => <li key={x} className="border-l-2 border-line pl-3">{x}</li>)}</ul>
              {/* Right connector on desktop */}
              <span aria-hidden="true" className="pointer-events-none absolute right-[-24px] top-1/2 hidden h-px w-6 -translate-y-1/2 bg-signal/60 lg:block" />
            </div>
          </R>

          {/* Center hub with SVG connectors */}
          <R d={0.12}>
            <div className="relative flex justify-center">
              <svg aria-hidden="true" className="pointer-events-none absolute inset-0 hidden h-full w-full lg:block" viewBox="0 0 200 200" preserveAspectRatio="none">
                <motion.circle cx="30" cy="100" r="4" fill="#D2378B" animate={{ cx: [30, 100] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} />
                <motion.circle cx="170" cy="100" r="4" fill="#D2378B" animate={{ cx: [170, 100] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 1.1 }} />
              </svg>
              <div className="relative flex h-40 w-40 items-center justify-center rounded-full border border-signal/40 bg-ink-2/70">
                <span className="pulse-ring absolute inset-0 rounded-full border border-signal/40" />
                <div className="text-center">
                  <p className="font-mono text-[10px] tracking-[0.24em] text-signal">TRIFINITY</p>
                  <p className="mt-2 font-display text-lg font-light text-white">One connected picture</p>
                </div>
              </div>
            </div>
          </R>

          <R d={0.2}>
            <div data-testid="trifinity-life-side" className="relative rounded-xl border border-line bg-ink-2/50 p-6">
              <p className="font-mono text-[10px] tracking-[0.22em] text-signal">EVERYDAY LIFE</p>
              <ul className="mt-4 space-y-2 text-sm text-fog">{["Nutrition", "Training", "Sleep"].map((x) => <li key={x} className="border-l-2 border-line pl-3">{x}</li>)}</ul>
              <span aria-hidden="true" className="pointer-events-none absolute left-[-24px] top-1/2 hidden h-px w-6 -translate-y-1/2 bg-signal/60 lg:block" />
            </div>
          </R>
        </div>

        <R d={0.25}>
          <div className="mt-12 text-center">
            <a href="#trifinity-contact" data-testid="trifinity-clinic-cta" className="inline-block bg-white px-8 py-4 font-mono text-[11px] tracking-[0.16em] text-ink transition-colors duration-300 hover:bg-signal hover:text-white">BRING TRIFINITY TO YOUR CLINIC</a>
          </div>
        </R>
      </div>
    </section>

    {/* 04 LOOP · pinned scroll-driven highlight */}
    <LoopScrollTrack items={LOOP} />

    {/* 05 FINAL CTA · B2B contact form */}
    <section id="trifinity-contact" data-testid="trifinity-contact-section" className="px-6 py-24 md:px-12 md:py-32">
      <div className="mx-auto max-w-3xl">
        <R>
          <h3 data-testid="trifinity-final-headline" className="text-center font-display text-3xl font-light md:text-4xl">Everything connects.</h3>
          <p className="mt-4 text-center font-mono text-[10px] tracking-[0.18em] text-ash">FOR CLINICS AND HEALTHCARE PARTNERS</p>
          <div className="mt-10"><B2BForm /></div>
        </R>
      </div>
    </section>
  </main>
);
};

export default Trifinity;
